import React from 'react';
import { TextField, FormControl, Select, MenuItem, IconButton, Tooltip, Typography, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlined';
import PeriodSelector from './PeriodSelector';
import ViewSection, { FieldLabel } from './ViewSection';

const CONTRATS_AVEC_DATE_FIN = ['cdd', 'freelance'];
const CONTRATS_AVEC_PERIODE = ['stage'];

const datePickerSlotProps = { textField: { size: 'small', fullWidth: true } };

const subBoxSx = {
  mt: 2,
  p: 2,
  borderRadius: '12px',
  bgcolor: '#f8fbfe',
  border: '1px solid #e2edf7',
};

const ContractDetailsCard = ({
  employee,
  handleChange,
  contratOptions,
  setOpenAddContrat,
  setAddContratOrigin,
  contratNom,
  isRecentIntegration,
  getDefaultDateFin,
  contractEndMessage,
  disabled
}) => {
  const shouldDisableContractFields = disabled || !!contractEndMessage;

  const renderContractSpecifics = () => {
    if (contratNom === 'cdi' && (!employee.nombreRenouvellements || Number(employee.nombreRenouvellements) === 0)) {
      return (
        <Box sx={subBoxSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box>
              <FieldLabel>Période d’essai</FieldLabel>
              <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mt: -0.4 }}>
                Durée de la période d’essai du CDI.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>Renouvelable</Typography>
              <Box sx={{ display: 'flex', p: 0.25, borderRadius: '8px', bgcolor: '#eef2f7' }}>
                {[{ v: true, l: 'Oui' }, { v: false, l: 'Non' }].map((o) => {
                  const active = !!employee.renouvellements === o.v;
                  return (
                    <Box
                      key={o.l}
                      component="button"
                      type="button"
                      disabled={shouldDisableContractFields}
                      onClick={() => handleChange('renouvellements', o.v)}
                      sx={{
                        border: 'none',
                        cursor: shouldDisableContractFields ? 'not-allowed' : 'pointer',
                        px: 1.5,
                        py: 0.4,
                        borderRadius: '6px',
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        fontFamily: 'inherit',
                        bgcolor: active ? '#03407b' : 'transparent',
                        color: active ? '#fff' : '#64748b',
                        transition: 'all .15s',
                      }}
                    >
                      {o.l}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
          <PeriodSelector
            selectedPeriod={employee.periodeContrat || ''}
            onSelectPeriod={(newValue) => handleChange('periodeContrat', newValue)}
            isCDI={true}
            disabled={shouldDisableContractFields}
          />
        </Box>
      );
    }

    if (CONTRATS_AVEC_PERIODE.includes(contratNom)) {
      return (
        <Box sx={subBoxSx}>
          <FieldLabel>Période de contrat</FieldLabel>
          <PeriodSelector
            selectedPeriod={employee.periodeContrat || ''}
            onSelectPeriod={(newValue) => handleChange('periodeContrat', newValue)}
            disabled={shouldDisableContractFields}
          />
        </Box>
      );
    }

    if (CONTRATS_AVEC_DATE_FIN.includes(contratNom)) {
      const recent = isRecentIntegration();
      return (
        <Box sx={subBoxSx}>
          <FieldLabel>Date fin contrat</FieldLabel>
          <DatePicker
            value={recent ? getDefaultDateFin() : (employee.dateFinContrat ? new Date(employee.dateFinContrat) : null)}
            onChange={(newValue) => {
              if (!recent) handleChange('dateFinContrat', newValue?.toISOString().split('T')[0]);
            }}
            disabled={shouldDisableContractFields}
            slotProps={datePickerSlotProps}
          />
          {recent && (
            <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mt: 0.75 }}>
              Intégration récente : la date de fin est calculée automatiquement (3 mois après l’intégration).
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <ViewSection
      icon={<WorkOutlineIcon />}
      title="Contrat & poste"
      subtitle="Type de contrat, fonction et dates clés"
      sx={{ height: '100%' }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FieldLabel>Contrat</FieldLabel>
              <Tooltip title="Ajouter un type de contrat">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setAddContratOrigin('mainForm');
                      setOpenAddContrat(true);
                    }}
                    disabled={shouldDisableContractFields}
                    sx={{ p: 0.25, mt: -0.75, color: '#03407b', '&:hover': { bgcolor: '#ebf4fb' } }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <FormControl fullWidth size="small">
              <Select
                value={employee.contratId || ''}
                displayEmpty
                onChange={(e) => {
                  const contratId = e.target.value;
                  const selected = contratOptions.find(c => c.id === contratId);
                  handleChange('contratId', contratId);
                  if (selected?.nom_contrat.toLowerCase() === 'cdi') {
                    handleChange('periodeContrat', '3mois');
                  } else {
                    handleChange('periodeContrat', '');
                  }
                }}
                disabled={shouldDisableContractFields}
              >
                <MenuItem value="">Sélectionner...</MenuItem>
                {contratOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontWeight: employee.contratId === c.id ? 700 : 400 }}>
                    {c.nom_contrat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FieldLabel required>Fonction</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Ex : Développeur Full Stack"
              value={employee.fonction || ''}
              onChange={e => handleChange('fonction', e.target.value)}
              disabled={shouldDisableContractFields}
            />
          </Box>

          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <FieldLabel>Date d'intégration</FieldLabel>
            <DatePicker
              value={employee.dateIntegration ? new Date(employee.dateIntegration) : null}
              onChange={(newValue) =>
                handleChange('dateIntegration', newValue ? newValue.toISOString().substring(0, 10) : '')
              }
              disabled={shouldDisableContractFields}
              slotProps={datePickerSlotProps}
            />
          </Box>
        </Box>

        {renderContractSpecifics()}
      </LocalizationProvider>
    </ViewSection>
  );
};

export default ContractDetailsCard;
